import { effect, inject, Injector, untracked } from "@angular/core";
import { NgEventBus } from "ng-event-bus";
import { TypedDialogService } from "@tenzu/utils/services/typed-dialog-service/typed-dialog.service";
import PortalComponent from "./portal.component";
import { matDialogConfig } from "@tenzu/utils/mat-config";
import { ToolBarStore } from "@tenzu/repository/toolbar/toolBarStore";
import { Plugin, PLUGINS_TOKEN } from "../../app/app.config";
import { HttpClient } from "@angular/common/http";
import { CustomerInfo, PortalStore } from "./portal.store";
import { UserStore } from "@tenzu/repository/user/user.store";
import { toObservable } from "@angular/core/rxjs-interop";
import { filterNotNull } from "@tenzu/utils/functions/rxjs.operators";
import { filter, Observable, tap } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { ConfigModelPortal } from "./providers";
import { EnvironmentConfig } from "../../environments/environment-type";
import { BaseDataModel } from "@tenzu/repository/base/misc.model";
import { ConfigAppService } from "@tenzu/repository/config-app/config-app.service";

export default class PortalService implements Plugin {
  injector = inject(Injector);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly configAppService = inject(ConfigAppService);
  public readonly portalStore = inject(PortalStore);
  private readonly eventBus = inject(NgEventBus);
  private readonly typedDialogService = inject(TypedDialogService);
  private readonly toolBarStore = inject(ToolBarStore);
  public customerInfo = this.portalStore.customerInfo;
  private customerInfoUrl = `${this.configAppService.apiUrl()}/portal/customer`;
  private readonly userStore = inject(UserStore);
  private refModal: MatDialogRef<unknown> | null = null;
  private openPending = false;

  constructor() {
    const config = this.configAppService.config() as EnvironmentConfig & ConfigModelPortal;
    if (!config.portal) {
      return;
    }
    this.route.queryParams.pipe(filter((params) => params["portalPaymentSuccess"])).subscribe(() => {
      this.portalStore.setSuccess();
    });
    toObservable(this.userStore.myUser)
      .pipe(
        filterNotNull(),
        switchMap(() => this.getCustomerInfo()),
      )
      .subscribe();
    effect(() => {
      const opened = this.portalStore.modal.opened();
      const success = this.portalStore.modal.success();
      untracked(() => {
        if (opened || success) {
          this.openPortal(success);
        } else {
          this.refModal?.close();
          this.refModal = null;
        }
      });
    });
    this.toolBarStore.setItems([
      {
        iconName: "favorite",
        label: "portal.button_label",
        eventName: "open-tenzu-front-portal",
        eventData: { closable: true },
      },
    ]);
    this.eventBus.on("open-tenzu-front-portal").subscribe((event) => {
      this.portalStore.openModal(event.data as boolean);
    });
  }
  openPortal(success: boolean) {
    if (this.refModal || this.openPending) {
      return;
    }
    this.openPending = true;
    this.typedDialogService
      .openWhenIdle(PortalComponent, {
        ...matDialogConfig,
        data: { success: success },
        width: "80vw",
        height: "80vh",
        maxWidth: "80vw",
        closeOnNavigation: false,
        disableClose: !this.portalStore.modal().closable,
      })
      .subscribe((ref) => {
        this.openPending = false;
        if (!this.portalStore.modal.opened() && !this.portalStore.modal.success()) {
          ref.close();
          return;
        }
        this.refModal = ref;
        ref.afterClosed().subscribe(() => this.closeModal());
      });
  }
  getCustomerInfo(): Observable<CustomerInfo> {
    return this.http.get<BaseDataModel<CustomerInfo>>(this.customerInfoUrl).pipe(
      map((dataObject) => dataObject.data),
      tap((customerInfo) => {
        this.portalStore.set(customerInfo);
        if (this.portalStore.customerInfo().shouldDisplaySupportNotification) {
          this.portalStore.openModal();
        }
      }),
    );
  }
  ignoreModal() {
    const ignoreUrl = `${this.configAppService.apiUrl()}/portal/ignore`;
    this.http.post<void>(ignoreUrl, {}).subscribe();
    this.closeModal();
  }
  closeModal() {
    this.portalStore.closeModal();
  }

  goToCustomerPortal(returnUrl: string) {
    const customerPortalUrl = `${this.configAppService.apiUrl()}/portal/create-customer-portal-session`;
    const value = {
      returnUrl: returnUrl,
    };
    this.http
      .post<{ url: string }>(customerPortalUrl, value)
      .pipe(tap((res) => (window.location.href = res.url)))
      .subscribe();
  }

  submit(value: { priceAmount: number; recurrent: boolean }) {
    const result = { ...value };
    const checkoutUrl = `${this.configAppService.apiUrl()}/portal/create-checkout-session`;
    if (result.priceAmount) {
      result.priceAmount = Math.round(value.priceAmount * 100); // get value in cents and get rid of eventual leftover decimals
      this.http
        .post<{ url: string }>(checkoutUrl, { ...result, returnUrl: this.router.url })
        .pipe(tap((res) => (window.location.href = res.url)))
        .subscribe();
    }
  }
}

export function injectPortailService() {
  const token = inject(PLUGINS_TOKEN);
  const portalService = token.find((item) => item instanceof PortalService);
  if (portalService) {
    return portalService;
  } else {
    throw new Error("PortalService must be provided");
  }
}
